import React, { useEffect, useState } from "react";
import { Button, Input, Modal, Tooltip, Spin, Pagination } from "antd";
import DeleteModalBtn from "../../assets/images/delete-icon-modal.svg";
import { Severty, ShowToast } from "../../helper/toast";
import { useNavigate, useParams } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { EyeOutlined, EditOutlined, DeleteOutlined, LinkOutlined } from "@ant-design/icons";

import moment from "moment";

// Permission helper
const getPermissionByName = (moduleName) => {
  try {
    const user = JSON.parse(localStorage.getItem("admin_userProfile") || "{}");
    if (user.type === "Admin") {
      return { view: true, add: true, edit: true, delete: true };
    }
    // Case/whitespace-insensitive match — real stored role data is
    // inconsistent (e.g. "blogs" lowercase vs "Blogs", trailing spaces on
    // "Research Assistant "/"Writing Assistant "). An exact === match here
    // silently returns {} (no permission) for a legitimately-permitted
    // account whenever casing/whitespace doesn't line up byte-for-byte.
    // Mirrors viewclients.js's already-fixed version of this same helper
    // and the backend's checkPermission normalization fix.
    const normalizedTarget = String(moduleName || "").trim().toLowerCase();
    const permissions = user?.roleId?.permission || [];
    return (
      permissions.find(
        (p) => String(p?.name || "").trim().toLowerCase() === normalizedTarget
      ) || {}
    );
  } catch {
    return {};
  }
};

const getScheduledMoment = (blog) => {
  if (!blog?.selectedDate) return null;

  const datePart = moment(blog.selectedDate).format("YYYY-MM-DD");
  const timePart = String(blog?.selectedTime || "00:00").slice(0, 5);
  const scheduledAt = moment(
    `${datePart} ${timePart}`,
    "YYYY-MM-DD HH:mm",
    true
  );

  return scheduledAt.isValid() ? scheduledAt : null;
};

const isBlogScheduledForFuture = (blog) => {
  if (String(blog?.status || "").toLowerCase() !== "scheduled") return false;

  const scheduledAt = getScheduledMoment(blog);
  return !scheduledAt || scheduledAt.isAfter(moment());
};

const isBlogPastDue = (blog) => {
  const scheduledAt = getScheduledMoment(blog);
  return scheduledAt?.isValid() && scheduledAt.isSameOrBefore(moment());
};

const isBlogPublished = (blog) => Boolean(blog?.postId);

const isBlogDelayed = (blog) => {
  if (isBlogPublished(blog)) return false;

  const normalizedStatus = String(
    blog?.brandStatus || blog?.clientStatus || ""
  ).toLowerCase();

  return isBlogPastDue(blog) && normalizedStatus.includes("publish");
};

const formatStatusLabel = (status) => {
  if (!status) return "Draft";

  const normalizedStatus = String(status).toLowerCase();
  if (normalizedStatus === "publish") return "Published";
  if (normalizedStatus === "publish by client") return "Published by Client";

  return String(status)
    .split(" ")
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
    )
    .join(" ");
};

const getPublishedStatusLabel = (blog) => {
  const rawStatus = blog?.brandStatus || blog?.clientStatus;
  const normalizedStatus = String(rawStatus || "").toLowerCase();

  if (normalizedStatus === "publish by client") return "Published by Client";
  if (normalizedStatus === "publish by brand") return "Published by Brand";
  if (normalizedStatus === "publish by admin" || normalizedStatus === "publish") {
    return "Published";
  }

  return "Published";
};

const getBlogDisplayStatus = (blog) => {
  const rawStatus = blog?.brandStatus || blog?.clientStatus;
  const normalizedStatus = String(rawStatus || "").toLowerCase();

  if (isBlogScheduledForFuture(blog)) {
    return {
      label: "Scheduled",
      color: "#FFF2CC",
      textColor: "#B35C00",
      borderColor: "#FAAD14",
    };
  }

  if (isBlogDelayed(blog)) {
    return {
      label: "Delayed",
      color: "#FFF1F0",
      textColor: "#CF1322",
      borderColor: "#FFCCC7",
    };
  }

  if (isBlogPublished(blog)) {
    return {
      label: getPublishedStatusLabel(blog),
      color: "#E6F4FF",
      textColor: "#0958D9",
      borderColor: "#91CAFF",
    };
  }

  if (normalizedStatus.includes("publish")) {
    return {
      label: "Publish Pending",
      color: "#FFF7E6",
      textColor: "#AD6800",
      borderColor: "#FFD591",
    };
  }

  if (normalizedStatus.includes("review")) {
    return {
      label: formatStatusLabel(rawStatus),
      color: "#FFF2E8",
      textColor: "#C2410C",
      borderColor: "#FFBB96",
    };
  }

  if (normalizedStatus.includes("approve")) {
    return {
      label: formatStatusLabel(rawStatus),
      color: "#F6FFED",
      textColor: "#237804",
      borderColor: "#95DE64",
    };
  }

  return {
    label: formatStatusLabel(rawStatus),
    color: "#FAFAFA",
    textColor: "#595959",
    borderColor: "#D9D9D9",
  };
};

const stripHtml = (html = "") => {
  try {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  } catch {
    return html.replace(/<[^>]+>/g, "");
  }
};

const getBlogDirectUrl = (blog) =>
  blog?.articlePublicUrl ||
  blog?.link ||
  blog?.linkedinPostUrl ||
  blog?.linkedinShareUrl ||
  "";

const BlogListRow = ({
  blog,
  onViewEdit,
  onDelete,
  onOpenBlog,
  canDelete,
  canEdit,
}) => {
  const isOpen = false;
  const displayStatus = getBlogDisplayStatus(blog);
  const isManual = blog.blogType === "manual" || !!blog.blogWriteRequestId;
  const plainContent = stripHtml(blog.content || "");
  const contentPreview =
    plainContent.length > 130 ? `${plainContent.slice(0, 130)}...` : plainContent;
  const cleanOutline = stripHtml(blog.outline || "").trim();
  const shouldShowOutline = cleanOutline && cleanOutline.toLowerCase() !== "t";
  const outlinePreview =
    shouldShowOutline && cleanOutline.length > 110
      ? `${cleanOutline.slice(0, 110)}...`
      : shouldShowOutline
      ? cleanOutline
      : "";

  return (
    <div className="blr-wrapper">
      <div className="blr-row">
        <span className="blr-bar" style={{ backgroundColor: displayStatus.textColor }} />
        <div className="blr-content">
          <div className="blr-top">
            <span className={`blr-type-badge ${isManual ? "blr-type-manual" : "blr-type-ai"}`}>
              {isManual ? "Manual" : "AI"}
            </span>
            <span className="blr-status-dot" style={{ backgroundColor: displayStatus.textColor }} />
            <span className="blr-status-label" style={{ color: displayStatus.textColor }}>
              {displayStatus.label}
            </span>
          </div>
          <div className="blr-title">{blog.topic || "--"}</div>
          {outlinePreview ? <div className="blr-outline">{outlinePreview}</div> : null}
          {contentPreview ? <div className="blr-preview">{contentPreview}</div> : null}
          <div className="blr-meta">
            <span>
              {blog.updated_at
                ? moment(blog.updated_at).format("DD MMM YYYY")
                : moment(blog.created_at).format("DD MMM YYYY")}
            </span>
            {blog.selectedDate ? (
              <>
                <span className="blr-meta-sep">.</span>
                <span>Publish {moment(blog.selectedDate).format("DD MMM YYYY")}</span>
              </>
            ) : null}
          </div>
        </div>
        <div className="blr-actions" onClick={(e) => e.stopPropagation()}>
          {getBlogDirectUrl(blog) ? (
            <Tooltip title="Open Blog">
              <button type="button" className="blr-action-btn" onClick={() => onOpenBlog(blog)}>
                <LinkOutlined />
              </button>
            </Tooltip>
          ) : null}
          <Tooltip title={canEdit ? "Edit" : "View"}>
            <button type="button" className="blr-action-btn" onClick={() => onViewEdit(blog)}>
              {canEdit ? <EditOutlined /> : <EyeOutlined />}
            </button>
          </Tooltip>
          {canDelete ? (
            <Tooltip title="Delete">
              <button
                type="button"
                className="blr-action-btn blr-action-btn-delete"
                onClick={() => onDelete(blog)}
              >
                <DeleteOutlined />
              </button>
            </Tooltip>
          ) : null}
        </div>
      </div>
      {isOpen ? (
        <div className="blr-expanded">
          <div className="blr-expanded-grid">
            {shouldShowOutline ? (
              <div className="blr-expanded-section">
                <div className="blr-expanded-label">Outline</div>
                <div className="blr-expanded-text">{cleanOutline}</div>
              </div>
            ) : null}
            {plainContent ? (
              <div className="blr-expanded-section">
                <div className="blr-expanded-label">Content Preview</div>
                <div className="blr-expanded-text">
                  {plainContent.length > 400
                    ? `${plainContent.slice(0, 400)}...`
                    : plainContent}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ClientBlogTabs = ({ dashboardView = "" }) => {
  const navigate = useNavigate();
  const blogsPermission = getPermissionByName("blogs");
  
  // Redirect if user lacks view permission
  useEffect(() => {
    if (!blogsPermission?.view) {
      navigate("/dashboard");
    }
  }, [blogsPermission?.view]);
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [comment, setComment] = useState("");
  const [postId, setPostId] = useState("");
  const [loading, setLoading] = useState(false);
  const { request } = useRequest();
  const fetchData = () => {
    setLoading(true);
    let url = "/admin/access-module/blog/" + id;
    const query = [];
    if (searchText) query.push(`name=${encodeURIComponent(searchText)}`);
    if (dashboardView) query.push(`status=${encodeURIComponent(dashboardView)}`);
    if (selectedDate)
      query.push(
        `date=${encodeURIComponent(selectedDate.format("YYYY-MM-DD"))}`
      );
    query.push(`page=${currentPage}`);
    query.push(`limit=${pageSize}`);
    if (query.length) url += "?" + query.join("&");

    request({
      url,
      method: "GET",
      onSuccess: (response) => {
        if (response) {
          setData(response.data.blogs);
          setTotal(response.data.total || 0);
          setLoading(false);
        } else {
          ShowToast(
            response.message || "Failed to fetch blogs",
            Severty.ERROR
          );
          setLoading(false);
        }
      },
      onError: (error) => {
        console.error("Error fetching blogs:", error);
        ShowToast("Error fetching blog data", Severty.ERROR);
        setLoading(false);
      },
    });
  };

  // 👉 Call this when status is clicked
  const handleStatusClick = (status, record) => {
    setSelectedStatus(status);
    setIsModalOpen(true);
    setPostId(record);
  };

  // 👉 Call this when Review or Reject is clicked
  const handleAction = (action, record) => {
    console.log("Action:", action);
    console.log("Comment:", comment);
    request({
      url: `/admin/access-module/post-status/${postId}`,
      method: "PUT",
      data: { status: action, comment },
      onSuccess: (response) => {
        fetchData();
      },
    });
    setIsModalOpen(false);
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, searchText, selectedDate, dashboardView]);

  const deleteContent = () => {
    request({
      url: `/admin/access-module/blog/delete-blog/${selected?._id}`, // ✅ FIXED HERE
      method: "DELETE",
      onSuccess: (data) => {
        if (data.status === 200) {
          ShowToast(data.message, Severty.SUCCESS);
          setDeleteModalVisible(false);
          fetchData();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (data) => {
        ShowToast(data?.message || "Something went wrong", Severty.ERROR);
      },
      // onFinally: () => setLoading(false),
    });
  };

  const handleViewEdit = (record) => {
    if (blogsPermission?.edit) {
      navigate(`/create-new-manully-blog/${id}`, {
        state: { record: record, isEdit: true },
      });
      return;
    }
    setSelected(record);
    setViewModalVisible(true);
  };

  const handleOpenBlog = (blog) => {
    const url = getBlogDirectUrl(blog);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Spin spinning={loading}>
        <div className="blr-list-container">
          {data.length === 0 && !loading ? (
            <div className="blr-empty">No blogs found.</div>
          ) : (
            data.map((blog) => (
              <BlogListRow
                key={blog._id}
                blog={blog}
                onViewEdit={handleViewEdit}
                onDelete={(record) => {
                  setSelected(record);
                  setDeleteModalVisible(true);
                }}
                onOpenBlog={handleOpenBlog}
                canDelete={blogsPermission?.delete}
                canEdit={blogsPermission?.edit}
              />
            ))
          )}
        </div>
      </Spin>
      {total > pageSize ? (
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Pagination
            current={currentPage}
            total={total}
            pageSize={pageSize}
            showSizeChanger={false}
            onChange={(page) => setCurrentPage(page)}
          />
        </div>
      ) : null}
      <Modal
        className="table_modal delete-modal"
        footer={false}
        title={false}
        width={450}
        visible={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onOk={() => {
          deleteContent();
          setDeleteModalVisible(false);
        }}
        okText="Yes, confirm"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <div className="delete-modal-body">
          <img src={DeleteModalBtn} alt="delete" />
          <h3>Are you sure?</h3>
          <p> Are you sure you want to delete this blog? </p>
          <Button className="delete-fill-btn" onClick={deleteContent}>
            Yes, confirm
          </Button>
        </div>
      </Modal>

      <Modal
        width={757}
        className="table_modal"
        title="Post Details"
        visible={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
      >
        <div className="piller-view-detail">
          <div className="pill-detail-list">
            <h6>Title:</h6>
            <span>{selected?.topic || "N/A"}</span>
          </div>

          <div className="pill-detail-list">
            <h6>Post Text:</h6>
            <span>{selected?.content || "N/A"}</span>
          </div>

          <div className="pill-detail-list">
            <h6>Status:</h6>
            <span>{selected?.status || "N/A"}</span>
          </div>

          <div className="pill-detail-list">
            <h6>Last Updated:</h6>
            <span>
              {selected?.updated_at
                ? moment(selected.updated_at).format("YYYY-MM-DD HH:mm:ss")
                : "N/A"}
            </span>
          </div>
          {/* 
          <div className="pill-detail-list">
            <h6>Media:</h6>
            {selected?.media?.length > 0 ? (
              <img

                src={selected.media[0]}
                alt={`Media`}
                style={{
                  width: "60px",
                  height: "60px",
                  objectFit: "cover",
                  marginRight: "5px",
                  borderRadius: "5px",
                }}
              />
            ) : (
              <span>N/A</span>
            )}
          </div> */}
        </div>
      </Modal>
      <Modal
        title="Add Comment"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <Input.TextArea
          rows={4}
          placeholder="Enter your comment..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <div style={{ marginTop: 16, textAlign: "right" }}>
          {blogsPermission?.edit && (
            <Button
              type="primary"
              style={{ marginRight: 8 }}
              onClick={() => handleAction("Review")}
            >
              Review
            </Button>
          )}
          {blogsPermission?.edit && (
            <Button danger onClick={() => handleAction("Reject")}>
              Reject
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ClientBlogTabs;
